import { memo } from 'react';
import {
  Calendar,
  CalendarCell,
  CalendarGrid,
  DateInput,
  DatePicker as RACDatePicker,
  DatePickerProps as RACDatePickerProps,
  DateSegment,
  DateValue,
  Dialog,
  Group,
  Heading,
} from 'react-aria-components';

import { merge } from '@/components';
import { Button, Popover, Text } from '@/components/core';
import { ChevronIcon } from '@/components/icons';

export type DatePickerProps = RACDatePickerProps<DateValue> & {
  label: string;
  date?: DateValue | null;
  onDateChange?: (value: DateValue | null) => void;
};

const IDatePicker = ({ label, date, onDateChange, className, ...rest }: DatePickerProps) => {
  return (
    <RACDatePicker aria-label={label} className={className} defaultValue={date} {...rest}>
      <Group className="flex items-center">
        <Button variant="plain">
          <Text slot="description" className="pointer-events-none flex">
            <DateInput>
              {(segment) => {
                segment.isEditable = false;
                return <DateSegment segment={segment} />;
              }}
            </DateInput>
          </Text>
        </Button>
      </Group>
      <Popover>
        <Dialog>
          <Calendar onChange={onDateChange}>
            <header className="flex items-center justify-center gap-1 p-2">
              <Button variant="plain" slot="previous">
                <ChevronIcon rotation="left" />
              </Button>
              <Heading />
              <Button variant="plain" slot="next">
                <ChevronIcon rotation="right" />
              </Button>
            </header>
            <CalendarGrid>
              {(date) => (
                <CalendarCell
                  className={merge([
                    'data-[disabled]:bg-gray-100',
                    'data-[disabled]:text-gray-400',
                    'data-[selected]:bg-primary-100',
                    'data-[selection-end]:bg-primary-400',
                    'data-[selection-end]:outline-none',
                    'data-[selection-start]:bg-primary-400',
                    'data-[selection-start]:outline-none',
                    'p-3',
                    'cursor-pointer',
                    'rounded-lg',
                  ])}
                  date={date}
                />
              )}
            </CalendarGrid>
          </Calendar>
        </Dialog>
      </Popover>
    </RACDatePicker>
  );
};

export const DatePicker = memo(IDatePicker) as typeof IDatePicker;
